import type React from "react";

/**
 * Shared data-type classification + value-normalization helpers for KPI inputs.
 * Extracted verbatim from ActivityEntryForm so the activity entry form and the
 * nation template preview classify data types identically.
 */

export function isBooleanDataType(dataTypeName: string): boolean {
  const name = (dataTypeName || "").toLowerCase();
  return name.includes("bool");
}

export function isNumericDataType(dataTypeName: string): boolean {
  const name = (dataTypeName || "").toLowerCase();
  return [
    "int",
    "decimal",
    "number",
    "numeric",
    "float",
    "double",
    "percent",
    "percentage",
    "currency",
    "money",
  ].some((token) => name.includes(token));
}

export function isDateDataType(dataTypeName: string): boolean {
  const name = (dataTypeName || "").toLowerCase();
  return name.includes("date");
}

export function isSelectDataType(dataTypeName: string): boolean {
  return (dataTypeName || "").toUpperCase() === "SELECT";
}

export function isMultiselectDataType(dataTypeName: string): boolean {
  return (dataTypeName || "").toUpperCase() === "MULTISELECT";
}

export function isGridDataType(dataTypeName: string): boolean {
  return (dataTypeName || "").toUpperCase() === "GRID";
}

export function isCalculatedEntryMode(entryMode?: string | null): boolean {
  return (entryMode || "").toUpperCase() === "CALCULATED";
}

export function inputTypeFromDataType(dataTypeName: string): React.HTMLInputTypeAttribute {
  if (isNumericDataType(dataTypeName)) return "number";
  if (isDateDataType(dataTypeName)) return "date";
  return "text";
}

/** True only for known long-form text fields. */
export function isMultilineText(dataTypeName: string): boolean {
  const name = (dataTypeName || "").toLowerCase();
  if (isBooleanDataType(name)) return false;
  if (isNumericDataType(name) || isDateDataType(name)) return false;
  if (isSelectDataType(name) || isMultiselectDataType(name) || isGridDataType(name)) {
    return false;
  }
  return [
    "text",
    "comment",
    "description",
    "note",
    "remark",
    "message",
    "paragraph",
    "narrative",
  ].some((token) => name.includes(token));
}

export function normalizeValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

export function normalizeBooleanInputValue(v: unknown): string {
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return v === 1 ? "true" : "false";

  const normalized = String(v ?? "").trim().toLowerCase();
  if (normalized === "true" || normalized === "1" || normalized === "yes") return "true";
  if (normalized === "false" || normalized === "0" || normalized === "no" || normalized === "") return "false";
  return "false";
}

export function normalizeValueForInput(v: unknown, dataTypeName: string): string {
  if (isBooleanDataType(dataTypeName)) return normalizeBooleanInputValue(v);
  if (
    isSelectDataType(dataTypeName) ||
    isMultiselectDataType(dataTypeName) ||
    isGridDataType(dataTypeName)
  ) {
    if (v === null || v === undefined || v === "") return "";
    if (typeof v === "string") return v;
    try {
      return JSON.stringify(v);
    } catch {
      return "";
    }
  }
  return normalizeValue(v);
}

export function isBooleanSwitchChecked(v: unknown): boolean {
  return normalizeBooleanInputValue(v) === "true";
}

export type SelectKpiValue = {
  option_ids: number[];
  inputs?: Record<string, string>;
  option_labels?: Record<string, string>;
};

export type GridRowKind = "free" | "fixed";

export type GridKpiRow = {
  row_kind?: GridRowKind | string;
  row_key?: string;
  [columnKey: string]: string | number | null | undefined;
};

export type GridKpiValue = {
  rows: GridKpiRow[];
  column_labels?: Record<string, string>;
  column_order?: string[];
};

export type SelectSerializeOption = {
  option_id?: number;
  kpi_option_id?: number;
  label: string;
};

export type GridSerializeColumn = {
  column_key?: string | null;
  label: string;
  list_order?: number;
  is_active?: boolean;
};

/** Coerce a grid cell to a rollup number; BOOLEAN → 1/0. Returns null if empty/non-numeric. */
export function gridCellToRollupNumber(
  raw: unknown,
  dataTypeName?: string | null
): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const isBool = isBooleanDataType(dataTypeName || "");
  if (isBool) {
    if (typeof raw === "boolean") return raw ? 1 : 0;
    const s = String(raw).trim().toLowerCase();
    if (s === "true" || s === "1") return 1;
    if (s === "false" || s === "0") return 0;
    return null;
  }
  if (typeof raw === "boolean") return raw ? 1 : 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function parseSelectKpiValue(raw: unknown): SelectKpiValue {
  if (raw === null || raw === undefined || raw === "") {
    return { option_ids: [], inputs: {} };
  }
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { option_ids: [], inputs: {} };
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { option_ids: [], inputs: {} };
  }
  const obj = parsed as Record<string, unknown>;
  const optionIds = Array.isArray(obj.option_ids)
    ? obj.option_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
    : [];
  const inputs: Record<string, string> = {};
  if (obj.inputs && typeof obj.inputs === "object" && !Array.isArray(obj.inputs)) {
    for (const [k, v] of Object.entries(obj.inputs as Record<string, unknown>)) {
      inputs[String(k)] = v == null ? "" : String(v);
    }
  }
  const optionLabels: Record<string, string> = {};
  if (obj.option_labels && typeof obj.option_labels === "object" && !Array.isArray(obj.option_labels)) {
    for (const [k, v] of Object.entries(obj.option_labels as Record<string, unknown>)) {
      if (v != null && String(v).trim() !== "") optionLabels[String(k)] = String(v);
    }
  }
  return {
    option_ids: optionIds,
    inputs,
    ...(Object.keys(optionLabels).length ? { option_labels: optionLabels } : {}),
  };
}

export function serializeSelectKpiValue(
  value: SelectKpiValue,
  options?: SelectSerializeOption[] | null
): string {
  const optionLabels: Record<string, string> = { ...(value.option_labels ?? {}) };
  for (const opt of options ?? []) {
    const id = opt.kpi_option_id ?? opt.option_id;
    if (id == null) continue;
    const key = String(id);
    if (!optionLabels[key] && opt.label?.trim()) {
      optionLabels[key] = opt.label;
    }
  }
  const payload: SelectKpiValue = {
    option_ids: value.option_ids,
    inputs: value.inputs ?? {},
  };
  if (Object.keys(optionLabels).length > 0) {
    payload.option_labels = optionLabels;
  }
  return JSON.stringify(payload);
}

export function parseGridKpiValue(raw: unknown): GridKpiValue {
  if (raw === null || raw === undefined || raw === "") {
    return { rows: [] };
  }
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { rows: [] };
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { rows: [] };
  }
  const obj = parsed as Record<string, unknown>;
  const rows = obj.rows;
  if (!Array.isArray(rows)) return { rows: [] };

  const columnLabels: Record<string, string> = {};
  if (obj.column_labels && typeof obj.column_labels === "object" && !Array.isArray(obj.column_labels)) {
    for (const [k, v] of Object.entries(obj.column_labels as Record<string, unknown>)) {
      if (v != null && String(v).trim() !== "") columnLabels[String(k)] = String(v);
    }
  }
  const columnOrder = Array.isArray(obj.column_order)
    ? obj.column_order.map((k) => String(k)).filter(Boolean)
    : [];

  return {
    rows: rows.map((row) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) return {};
      const out: GridKpiRow = {};
      for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
        if (k === "row_kind" || k === "row_key") {
          if (v != null && String(v).trim() !== "") {
            out[k] = String(v);
          }
          continue;
        }
        out[k] = v == null ? "" : String(v);
      }
      return out;
    }),
    ...(Object.keys(columnLabels).length ? { column_labels: columnLabels } : {}),
    ...(columnOrder.length ? { column_order: columnOrder } : {}),
  };
}

export function serializeGridKpiValue(
  value: GridKpiValue,
  columns?: GridSerializeColumn[] | null
): string {
  const active = (columns ?? [])
    .filter((c) => c.is_active !== false)
    .slice()
    .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));

  const columnLabels: Record<string, string> = { ...(value.column_labels ?? {}) };
  const columnOrder: string[] = value.column_order?.length
    ? [...value.column_order]
    : [];

  for (const col of active) {
    const key = col.column_key || "";
    if (!key) continue;
    if (!columnLabels[key] && col.label?.trim()) {
      columnLabels[key] = col.label;
    }
    if (!columnOrder.includes(key)) {
      columnOrder.push(key);
    }
  }

  const payload: GridKpiValue = {
    rows: value.rows.map((row) => {
      const out: GridKpiRow = {};
      const kind = row.row_kind != null ? String(row.row_kind) : "";
      if (kind) out.row_kind = kind;
      const key = row.row_key != null ? String(row.row_key) : "";
      if (key) out.row_key = key;
      for (const [k, v] of Object.entries(row)) {
        if (k === "row_kind" || k === "row_key") continue;
        out[k] = v == null ? "" : v;
      }
      return out;
    }),
  };
  if (Object.keys(columnLabels).length > 0) {
    payload.column_labels = columnLabels;
  }
  if (columnOrder.length > 0) {
    payload.column_order = columnOrder;
  }
  return JSON.stringify(payload);
}
